import { useEffect } from 'react';
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronLeft, HardDrive, Camera, Activity } from 'lucide-react';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';
import useDemoPath from '@/hook/useDemoPath';
import PATH_PAGE from '@/router/path';
import { useGetVolume } from '@/service/gpu-instance/volume-hook';
import { VolumeStatusBadge, VolumeHealthIndicator } from '../../components/volume';

const VolumeDetailLayout = () => {
  const { volumeId } = useParams<{ volumeId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId, workspaceId } = useParamWorkspaceId();
  const { withDemoPrefix } = useDemoPath();

  const { data: volume, isLoading, error } = useGetVolume(volumeId || '');

  // Determine active tab from URL
  const getActiveTab = () => {
    if (location.pathname.includes('/snapshots')) return 'snapshots';
    return 'overview';
  };

  const activeTab = getActiveTab();

  const handleTabChange = (value: string) => {
    if (!volumeId) return;
    const paths = PATH_PAGE.dashboard.gpuInstance.volumeById(orgId, workspaceId, volumeId);
    const path = value === 'overview' ? paths.overview : paths.snapshots;
    navigate(withDemoPrefix(path));
  };

  const handleBack = () => {
    navigate(withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.volume(orgId, workspaceId)));
  };

  if (error) {
    return (
      <div className="container py-8">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <HardDrive className="h-12 w-12 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium">Volume not found</h3>
          <p className="text-sm text-muted-foreground mb-4">
            The requested volume could not be found.
          </p>
          <Button variant="outline" onClick={handleBack}>
            <ChevronLeft className="mr-2 h-4 w-4" />
            Back to Volumes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={handleBack}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {isLoading ? (
              <Skeleton className="h-8 w-48" />
            ) : (
              <h1 className="text-2xl font-bold tracking-tight">{volume?.name}</h1>
            )}
            {volume && (
              <>
                <VolumeStatusBadge status={volume.status} />
                <VolumeHealthIndicator health={volume.health} />
              </>
            )}
          </div>
          {isLoading ? (
            <Skeleton className="h-4 w-64" />
          ) : (
            <p className="text-muted-foreground pl-10">
              {volume?.requestedSize} GB {volume?.storageClass.displayName} volume
              {volume?.isAttached && ` attached to ${volume.attachedInstances.length} instance(s)`}
            </p>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="overview" className="gap-2">
            <Activity className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="snapshots" className="gap-2">
            <Camera className="h-4 w-4" />
            Snapshots
            {volume && volume.snapshotCount > 0 && (
              <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs">
                {volume.snapshotCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Page Content */}
      <Outlet context={{ volume, isLoading }} />
    </div>
  );
};

export default VolumeDetailLayout;
